import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Plus,
    Trash2,
    Pencil,
    X,
    Star,
    Info
} from 'lucide-react';
import { API } from '../../config';

const AdminFeatures = () => {
    const [features, setFeatures] = useState([]);
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingFeature, setEditingFeature] = useState(null);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        icon_name: 'ShieldCheckIcon',
        order: 0
    });

    useEffect(() => {
        fetchFeatures();
    }, []);

    const fetchFeatures = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API}/features`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setFeatures(response.data);
        } catch (error) {
            toast.error('Failed to fetch features');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            if (editingFeature) {
                await axios.put(`${API}/features/${editingFeature.id}`, formData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Feature updated!');
            } else {
                await axios.post(`${API}/features`, formData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Feature added!');
            }
            resetForm();
            fetchFeatures();
        } catch (error) {
            toast.error('Failed to save feature');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setShowAddForm(false);
        setEditingFeature(null);
        setFormData({ title: '', description: '', icon_name: 'ShieldCheckIcon', order: 0 });
    };

    const handleEdit = (feature) => {
        setEditingFeature(feature);
        setFormData({
            title: feature.title,
            description: feature.description,
            icon_name: feature.icon_name || 'ShieldCheckIcon',
            order: feature.order || 0
        });
        setShowAddForm(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this feature?')) return;
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API}/features/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Feature deleted!');
            fetchFeatures();
        } catch (error) {
            toast.error('Failed to delete feature');
        }
    };

    const iconOptions = [
        'ShieldCheckIcon', 'TruckIcon', 'HeartIcon', 'LeafIcon', 'AwardIcon', 'UsersIcon'
    ];

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-3xl shadow-sm p-8"
        >
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Why Choose Us Features</h2>
                    <p className="text-gray-500 text-sm mt-1">Manage core value propositions shown in the "Why Choose Us" section</p>
                </div>
                <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => { resetForm(); setShowAddForm(true); }}
                    className="bg-gray-900 text-white px-5 py-2.5 rounded-xl hover:bg-black transition-all shadow-sm flex items-center font-medium text-sm"
                >
                    <Plus className="h-4 w-4 mr-2" />
                    Add New Feature
                </motion.button>
            </div>

            {/* Form */}
            <AnimatePresence>
                {showAddForm && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden mb-8"
                    >
                        <div className="bg-gray-50 rounded-3xl p-8 border border-gray-200">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-lg font-bold text-gray-900">
                                    {editingFeature ? 'Edit Feature' : 'Add New Feature'}
                                </h3>
                                <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
                                    <X className="h-5 w-5" />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Feature Title</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.title}
                                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                        className="w-full px-4 py-2 rounded-xl border border-gray-300 outline-none focus:ring-2 focus:ring-gray-900"
                                        placeholder="e.g. 100% Organic"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                                    <textarea
                                        rows="2"
                                        required
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        className="w-full px-4 py-2 rounded-xl border border-gray-300 outline-none focus:ring-2 focus:ring-gray-900"
                                        placeholder="Briefly explain this feature..."
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Icon Type</label>
                                    <select
                                        value={formData.icon_name}
                                        onChange={(e) => setFormData({ ...formData, icon_name: e.target.value })}
                                        className="w-full px-4 py-2 rounded-xl border border-gray-300 outline-none focus:ring-2 focus:ring-gray-900"
                                    >
                                        {iconOptions.map(icon => (
                                            <option key={icon} value={icon}>{icon}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Display Order</label>
                                    <input
                                        type="number"
                                        value={formData.order}
                                        onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                                        className="w-full px-4 py-2 rounded-xl border border-gray-300 outline-none focus:ring-2 focus:ring-gray-900"
                                    />
                                </div>
                                <div className="md:col-span-2 flex gap-3 pt-4">
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="bg-gray-900 text-white px-8 py-2.5 rounded-xl font-medium disabled:opacity-50"
                                    >
                                        {loading ? 'Saving...' : 'Save Feature'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={resetForm}
                                        className="px-8 py-2.5 rounded-xl border border-gray-300 font-medium"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* List */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {features.map((feature) => (
                    <div key={feature.id} className="p-6 bg-gray-50 rounded-2xl border border-gray-100 group relative">
                        <div className="flex items-start gap-4">
                            <div className="p-3 bg-white rounded-xl shadow-sm">
                                <Star className="h-6 w-6 text-agro-green" />
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-900">{feature.title}</h3>
                                <p className="text-gray-500 text-sm mt-1">{feature.description}</p>
                            </div>
                        </div>

                        <div className="absolute top-4 right-4 flex opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => handleEdit(feature)} className="p-1.5 text-gray-400 hover:text-gray-900">
                                <Pencil className="h-4 w-4" />
                            </button>
                            <button onClick={() => handleDelete(feature.id)} className="p-1.5 text-gray-400 hover:text-red-500">
                                <Trash2 className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                ))}
                {features.length === 0 && (
                    <div className="col-span-full py-12 text-center text-gray-400 flex flex-col items-center">
                        <Info className="h-12 w-12 mb-3 opacity-20" />
                        <p>No features configured yet</p>
                    </div>
                )}
            </div>
        </motion.div>
    );
};

export default AdminFeatures;
